import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  styled,
  FormLabel,
  CircularProgress,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Invitation,
  InvitationState,
  UpdateInvitationRequest,
} from '../../../../../redux/invitations/invitation.model';
import { NormalizedGroup } from '../../../../../redux/groups/api/GroupAdapter';
import { getPolyglot } from '../../../../../i18n';
import { ErrorText } from '../../../../../theme/components/Forms';
import StoerkIdAPI from '../../../../../redux/stoerkID/StoerkIdAPI';
import { useShowErrorMessage, useShowMessage } from '../../../../../util/hooks';

interface SendInvitationModalProps {
  open: boolean;
  workspaceId: string;
  onClose: () => void;
  company: NormalizedGroup | undefined;
  isLoading: boolean;
}
const MAX_CHARACTERES = 500;

const validationSchema = () =>
  Yup.object().shape({
    email: Yup.string().required('freshwatch.invitation.modal.required_email'),
    companyName: Yup.string().required(
      'freshwatch.invitation.modal.required_company_name'
    ),
    message: Yup.string().max(
      MAX_CHARACTERES,
      'freshwatch.invitation.modal.message_max_length'
    ),
  });

const StyledFormLabel = styled(FormLabel)(({ theme }) => ({
  marginBottom: theme.spacing(0.5),
  display: 'block',
  color: theme.palette.text.primary,
}));

export const SendInvitationModal: React.FC<SendInvitationModalProps> = ({
  open,
  onClose,
  company,
  isLoading,
  workspaceId,
}) => {
  const polyglot = getPolyglot();
  const showMessage = useShowMessage();
  const showErrorMessage = useShowErrorMessage();
  const [sendInvitation] = StoerkIdAPI.useInviteUserMutation();

  const formik = useFormik({
    initialValues: {
      email: '',
      companyName: company?.name || '',
      message: '',
    },
    validationSchema: validationSchema(),
    onSubmit: () => {
      handleApprove();
    },
  });

  const handleApprove = async () => {
    formik.setTouched({
      email: true,
      companyName: true,
      message: true,
    });
    if (!formik.isValid) return;

    try {
      const result = await sendInvitation({
        workspaceId: workspaceId || '',
        registrationData: {
          companyGroupId: company?.id,
          email: formik.values.email,
          personalMessage: formik.values.message,
        },
      }).unwrap();
      showMessage(polyglot.t('freshwatch.invitation.send_success'));
      onClose();
    } catch (e) {
      showErrorMessage(e ?? polyglot.t('freshwatch.invitation.send_error'));
      return;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box sx={{ padding: '2rem 0' }}>
          <Typography variant="h2" gutterBottom>
            {polyglot.t('freshwatch.invitation.modal.invitation_title')}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ padding: 3 }}>
        <form onSubmit={formik.handleSubmit}>
          {/* Companies Field */}
          <Grid item xs={12} md={12}>
            <StyledFormLabel htmlFor="companiesList">
              {polyglot.t('freshwatch.invitation.modal.company')}
            </StyledFormLabel>
            <TextField
              fullWidth
              value={formik.values.companyName}
              variant="outlined"
              disabled={true}
              id={'company'}
              sx={{ mb: 2 }}
            ></TextField>
          </Grid>
          {/* Email Field */}
          <Grid item xs={12} md={12}>
            <StyledFormLabel htmlFor="email">
              {polyglot.t('freshwatch.invitation.detail.email')}*
            </StyledFormLabel>
            <TextField
              fullWidth
              id={'email'}
              required
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={
                formik.touched.email &&
                formik.errors.email && (
                  <ErrorText>{polyglot.t(formik.errors.email)}</ErrorText>
                )
              }
              variant="outlined"
              sx={{
                '& .MuiInputBase-input.Mui-disabled': {
                  WebkitTextFillColor: (theme) => theme.palette.text.secondary,
                },
              }}
            />
          </Grid>

          {/* Message Field */}
          <Grid item xs={12} md={12} sx={{ mt: 2 }}>
            <StyledFormLabel htmlFor="message">
              {polyglot.t('freshwatch.invitation.modal.message')}
            </StyledFormLabel>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              {polyglot.t('freshwatch.invitation.modal.message_helper')}
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={4}
              id={'message'}
              value={formik.values.message}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.message && Boolean(formik.errors.message)}
              helperText={
                formik.touched.message &&
                formik.errors.message && (
                  <ErrorText>
                    {polyglot.t(formik.errors.message, {
                      number: MAX_CHARACTERES,
                    })}
                  </ErrorText>
                )
              }
              variant="outlined"
              sx={{
                '& .MuiInputBase-input.Mui-disabled': {
                  WebkitTextFillColor: (theme) => theme.palette.text.secondary,
                },
              }}
            />
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              {formik.values.message.length}/{MAX_CHARACTERES}
            </Typography>
          </Grid>
          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
              <Button
                variant="outlined"
                fullWidth
                onClick={onClose}
                disabled={isLoading}
              >
                {polyglot.t('button.cancel')}
              </Button>

              <Button
                variant="contained"
                onClick={handleApprove}
                disabled={isLoading}
                fullWidth
              >
                {isLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  polyglot.t('freshwatch.invitation.modal.send_invitation')
                )}
              </Button>
            </Box>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SendInvitationModal;
